import type { LoadedImage } from './state';

/**
 * Photos are downscaled to this max dimension immediately on load, before
 * anything else touches them. Full-resolution camera photos (3000px+) held
 * twice in memory crashed mobile Safari in the prototype.
 */
export const MAX_DIMENSION = 1600;

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** JPEG thumbnail for an upload slot, from any decoded bitmap (initial load or after a crop). */
export function canvasToThumb(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/jpeg', 0.85);
}

/**
 * Decode a user-selected file into a downscaled offscreen canvas plus a
 * JPEG thumbnail. Rejects with a human-readable message on any failure.
 *
 * Note: drawing onto a canvas discards EXIF/metadata (step 6 will make the
 * export-side guarantee explicit), and the browser applies EXIF orientation
 * during decode so the canvas is already upright.
 */
export function loadImageFile(file: File): Promise<LoadedImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const probe = new Image();
      probe.onload = () => {
        try {
          const scale = Math.min(1, MAX_DIMENSION / Math.max(probe.width, probe.height));
          const w = Math.max(1, Math.round(probe.width * scale));
          const h = Math.max(1, Math.round(probe.height * scale));
          const off = document.createElement('canvas');
          off.width = w;
          off.height = h;
          const octx = off.getContext('2d');
          if (!octx) throw new Error('2D canvas context unavailable');
          octx.drawImage(probe, 0, 0, w, h);
          resolve({ bitmap: off, thumb: canvasToThumb(off), name: file.name });
        } catch (err) {
          reject(new Error('Resize step failed: ' + errMsg(err)));
        }
      };
      probe.onerror = () => reject(new Error('That file could not be decoded as an image.'));
      probe.src = reader.result as string;
    };
    reader.onerror = () =>
      reject(new Error('FileReader failed: ' + (reader.error ? reader.error.message : 'unknown')));
    reader.readAsDataURL(file);
  });
}
